import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useNavigate } from 'react-router-dom';
import { ShieldCheck, Fingerprint, Eye, EyeOff, Save, AlertCircle, Loader2, ArrowLeft, RefreshCw } from 'lucide-react';
import Logo from '../components/Logo';

const ResetPassword: React.FC = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(true);
  const [hasSession, setHasSession] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const initResetFlow = async () => {
      try {
        // 1. Check if we already have a session (Supabase might have auto-parsed it)
        const { data: { session: existingSession } } = await supabase.auth.getSession();
        
        if (existingSession) {
          setHasSession(true);
          setChecking(false);
          return;
        }

        // 2. Manual Parse Fallback (Crucial for HashRouter)
        // HashRouter URLs look like: /#/reset-password#access_token=...
        // We need to extract the parameters from the end of the URL
        const hash = window.location.hash;
        if (hash.includes('access_token=')) {
          // Extract everything after the last # or handle the double hash
          const paramsString = hash.includes('#access_token') 
            ? hash.split('#').pop() 
            : hash.split('&').find(p => p.includes('access_token')) || '';
          
          const params = new URLSearchParams(paramsString || '');
          const accessToken = params.get('access_token');
          const refreshToken = params.get('refresh_token');

          if (accessToken && refreshToken) {
            const { data, error: setSessionError } = await supabase.auth.setSession({
              access_token: accessToken,
              refresh_token: refreshToken,
            });

            if (!setSessionError && data.session) {
              setHasSession(true);
              setChecking(false);
              return;
            }
          }
        }

        // 3. Final wait for auth state change event
        const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
          if (session) {
            setHasSession(true);
            setChecking(false);
          }
        });

        // Timeout fallback
        setTimeout(() => {
          setChecking(false);
          subscription.unsubscribe();
        }, 2000);

      } catch (err) {
        console.error("Reset flow initialization error:", err);
        setChecking(false);
      }
    };

    initResetFlow();
  }, []);

  const handleReset = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!password || password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const { error: updateError } = await supabase.auth.updateUser({ password });
      if (updateError) throw updateError;

      if (user) {
        const { error: profileError } = await supabase
          .from('user_profiles')
          .upsert({
            id: user.id,
            pin: null,
            pdf_pin: null,
            updated_at: new Date().toISOString()
          });

        if (profileError) throw profileError;
      }
      
      alert("Password updated successfully.");
      await supabase.auth.signOut();
      setHasSession(false);
      window.location.hash = '/login';
      return;
    } catch (err: any) {
      setError(err.message || "Could not update password. This link may have expired.");
    } finally {
      setLoading(false);
    }
  };

  if (checking) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#f8fafc] p-6 text-center">
        <div className="relative mb-8">
          <div className="w-24 h-24 border-4 border-slate-100 border-t-amber-400 rounded-full animate-spin"></div>
          <ShieldCheck className="absolute inset-0 m-auto text-slate-900 w-10 h-10" />
        </div>
        <h2 className="text-slate-900 font-black text-lg uppercase tracking-tight">Loading</h2>
        <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.3em] mt-3 animate-pulse">Checking your reset link...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-6 bg-[#f8fafc]">
      <div className="max-w-md w-full">
        <div className="text-center mb-10">
          <Logo size={100} className="mx-auto mb-6 drop-shadow-2xl" />
          <h1 className="text-3xl font-black text-[#0f172a] tracking-tight">Update Password</h1>
          <p className="text-slate-400 font-black text-[10px] uppercase tracking-[0.4em] mt-3">Choose your new password</p>
        </div>

        <div className="bg-white p-10 rounded-[3.5rem] shadow-[0_40px_80px_-15px_rgba(0,0,0,0.06)] border border-slate-50 relative overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-1.5 bg-gradient-to-r from-amber-400 to-amber-600"></div>
          
          {!hasSession ? (
            <div className="text-center py-6">
              <div className="bg-red-50 w-20 h-20 rounded-[2.5rem] flex items-center justify-center mx-auto mb-8 border border-red-100 shadow-sm">
                <AlertCircle className="text-red-500 w-10 h-10" />
              </div>
              <h2 className="text-red-600 font-black text-xs uppercase tracking-widest mb-4">Link Expired</h2>
              <p className="text-slate-500 text-[10px] font-bold uppercase tracking-wider leading-relaxed mb-10 px-4">
                This link has expired or has already been used.
              </p>
              <button 
                onClick={() => navigate('/forgot-password')}
                className="w-full bg-[#0f172a] text-white py-5 rounded-[2rem] font-black text-[10px] uppercase tracking-[0.2em] shadow-xl flex items-center justify-center gap-3 active:scale-95 transition-all"
              >
                <RefreshCw size={16} /> Request New Link
              </button>
            </div>
          ) : (
            <form onSubmit={handleReset} className="space-y-8">
              <div className="bg-amber-50/50 p-4 rounded-2xl border border-amber-100 mb-2">
                <p className="text-[9px] font-black text-amber-700 uppercase tracking-widest text-center">Email verified</p>
              </div>

              <div>
                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3 ml-1">New Account Password</label>
                <div className="relative group">
                  <Fingerprint className="absolute left-6 top-1/2 -translate-y-1/2 text-slate-300 group-focus-within:text-[#0f172a] transition-colors w-5 h-5" />
                  <input 
                    type={showPassword ? "text" : "password"} 
                    required 
                    minLength={6}
                    autoFocus
                    className="w-full pl-16 pr-16 py-5 bg-slate-50 border border-transparent rounded-[2rem] focus:bg-white focus:border-slate-200 focus:ring-4 focus:ring-slate-50 outline-none text-slate-900 font-bold transition-all" 
                    placeholder="Set new secret key" 
                    value={password} 
                    onChange={(e) => setPassword(e.target.value)} 
                  />
                  <button 
                    type="button" 
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-6 top-1/2 -translate-y-1/2 text-slate-300 hover:text-slate-600 transition-colors"
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3 ml-1">Confirm Password</label>
                <div className="relative group">
                  <Fingerprint className="absolute left-6 top-1/2 -translate-y-1/2 text-slate-300 group-focus-within:text-[#0f172a] transition-colors w-5 h-5" />
                  <input 
                    type={showConfirmPassword ? "text" : "password"} 
                    required 
                    minLength={6}
                    className="w-full pl-16 pr-16 py-5 bg-slate-50 border border-transparent rounded-[2rem] focus:bg-white focus:border-slate-200 focus:ring-4 focus:ring-slate-50 outline-none text-slate-900 font-bold transition-all" 
                    placeholder="Repeat your new password" 
                    value={confirmPassword} 
                    onChange={(e) => setConfirmPassword(e.target.value)} 
                  />
                  <button 
                    type="button" 
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-6 top-1/2 -translate-y-1/2 text-slate-300 hover:text-slate-600 transition-colors"
                  >
                    {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>

              {error && (
                <div className="p-5 rounded-[2rem] bg-red-50 border border-red-100 flex items-center gap-4 animate-shake">
                  <AlertCircle size={20} className="text-red-500 shrink-0" />
                  <p className="text-red-600 text-[9px] font-black uppercase tracking-wider leading-tight">{error}</p>
                </div>
              )}

              <button 
                type="submit" 
                disabled={loading} 
                className="w-full bg-[#0f172a] text-amber-400 py-6 rounded-[2.5rem] font-black shadow-2xl flex items-center justify-center hover:scale-[1.01] active:scale-95 transition-all disabled:opacity-50 border border-white/5"
              >
                {loading ? (
                  <Loader2 className="w-6 h-6 animate-spin" />
                ) : (
                  <><Save className="mr-3 w-5 h-5" /> Save New Password</>
                )}
              </button>
            </form>
          )}
        </div>
        
        <button 
          onClick={() => navigate('/login')}
          className="mt-8 mx-auto flex items-center text-slate-400 hover:text-slate-600 font-black text-[10px] uppercase tracking-widest transition-colors"
        >
          <ArrowLeft size={14} className="mr-2" /> Back to Login
        </button>
      </div>
    </div>
  );
};

export default ResetPassword;
